import { SyncCombinatorGuard } from '../../guards/sync-combinator-guard/sync-combinator-guard';
import { OtherToolsLayoutComponent } from '../../../school/layouts/other-tools-layout/other-tools-layout.component';
import { PusherResolver } from '../../resolvers/pusher/pusher-resolver';
import { SchoolSsvFilterResolver } from '../../resolvers/school-ssv/school-ssv-filter/school-ssv-filter.resolver';
import { SchoolSsvSortResolver } from '../../resolvers/school-ssv/school-ssv-sort/school-ssv-sort.resolver';
import { RouteGuard } from '../../guards/route-guard/route-guard';
import { CurrentEntityGuard } from '../../guards/current-entity-guard/current-entity-guard';
import { TogglesGuard } from '../../guards/toggles-guard/toggles-guards';
import { CurrentUserGuard } from '../../guards/current-user-guard/current-user-guard';
import { ssvMatcher } from '../../url-matchers/ssv-matcher/ssv-matcher';
import { familyMatcher } from '../../url-matchers/family-matcher/family-matcher';
import { SchoolResourceResolver } from '../../resolvers/school-resource/school-resource.resolver';
import { SdcActivityGuardForProfile } from '../../guards/sdc/sdc-activity-guard/sdc-activity-guard-for-profile';
import { SdcProfileMatcher } from '../../url-matchers/sdc-profile-matcher/sdc-profile-matcher';
import { SchoolSingleColumnLayoutComponent } from '../../../school/layouts/school-single-column-layout/school-single-column-layout.component';
import { SettingsLayoutComponent } from '../../../shared/components/settings/settings-layout/settings-layout.component';
import { FeatureToggleGuard } from '../../guards/feature-toggle-guard/feature-toggle-guard';
import { GridStateResolver } from '../../resolvers/grid-state/grid-state.resolver';
import { NonEntityUserGuard } from '../../guards/non-entity-user-guard/non-entity-user-guard';
import { entityTypeMatcher } from '../../url-matchers/entity-type-matcher/entity-type-matcher';
import { entityResourceMatcher } from '../../url-matchers/entity-resource-matcher/entity-resource-matcher';
import { entityProfileMatcher } from '../../url-matchers/entity-profile-matcher/entity-profile-matcher';
import { entityIdFirstMatcher } from '../../url-matchers/entity-single-matcher/entity-id-first-matcher';
import { ShelterIdResolver } from '../../resolvers/shelter-id/shelter-id.resolver';
import { GlobalMadlibResolver } from '../../resolvers/global-madlib/global-madlib.resolver';
import { EntityTypeGuard } from '../../guards/entity-type-guard/entity-type-guard';
import { EntityIdFirstRedirectGuard } from '../../guards/entity-id-first-redirect-guard/entity-id-first-redirect-guard';
import { EntityResourceGuard } from '../../guards/entity-resource-guard/entity-resource-guard';
import { ENTITY_TYPE } from '../../../shared/typings/types/entity-type.types';
import { ENTITY_MODE } from '../../../shared/typings/types/entity-mode.types';
import { LayoutSwitcherComponent } from 'Src/ng2/shared/layouts/layout-switcher/layout-switcher.component';
import { UrlNavigationService } from 'Src/ng2/shared/services/url-navigation/url-navigation.service';
import { KimsTrackerAccessGuard } from '../../guards/kims-tracker-access-guard/kims-tracker-access-guard';
import { FeatureSet, PageTemplate } from 'Src/ng2/shared/typings/interfaces/mixpanel.interface';
import { DeferrableLoad } from '../../deferred-route-loads';
import { SchoolReadyGuard } from '../../guards/school-ready-guard/school-ready-guard';

export const opEntityRoute = {
  matcher: entityTypeMatcher,
  authenticationRequired: true,
  rolePermissions: ['all'],
  canActivate: [SyncCombinatorGuard],
  data: {
    // TogglesGuard omitted: no entityId on this snapshot, so it fetched toggles scoped to
    // `currentSchoolId: null` which the entity reset then wiped. Every page-bearing route sits
    // under entityResourceMatcher, whose chain fetches with the real id.
    guards: [RouteGuard, CurrentUserGuard, NonEntityUserGuard],
  },
  children: [
    {
      path: '',
      pathMatch: 'full',
      redirectTo: '/not-found',
    },
    // Match on entity id first path and prefix with `single`
    {
      matcher: entityIdFirstMatcher,
      canActivate: [EntityIdFirstRedirectGuard],
      children: [],
    },
    {
      matcher: entityResourceMatcher,
      canActivate: [SyncCombinatorGuard],
      canActivateChild: [EntityTypeGuard, SchoolReadyGuard],
      data: {
        // Order matters: SyncCombinatorGuard awaits these in sequence. CurrentEntityGuard's school
        // load resets the store when the entity changes, which has to happen before TogglesGuard
        // reloads toggles and before EntityResourceGuard evaluates and caches One Portal for this
        // context — otherwise the evaluation is made against, or wiped by, the outgoing context.
        //
        // PusherGuard omitted: PusherResolver below does the same work, so the guard only made
        // initializePusherListeners tear down and re-register on every navigation. It gated
        // nothing — it returned false only when realtime wiring threw.
        guards: [CurrentEntityGuard, CurrentUserGuard, TogglesGuard, EntityResourceGuard],
        refreshComponent: (future, curr) => {
          const { params: { entityId: futureId, entityType: futureType, networkId: futureNetwork } } = future;
          const { params: { entityId: currId, entityType: currType, networkId: currNetwork } } = curr;
          const futureSubId = UrlNavigationService.getSubEntityId(future, futureType);
          const currSubId = UrlNavigationService.getSubEntityId(curr, currType);
          const isDifferent = futureType !== currType || futureId !== currId || futureNetwork !== currNetwork || futureSubId !== currSubId;
          return isDifferent;
        },
      },
      resolve: {
        pusher: PusherResolver,
        shelter: ShelterIdResolver,
        filters: GlobalMadlibResolver,
      },
      children: [
        {
          path: '',
          pathMatch: 'full',
          redirectTo: 'home',
        },
        {
          matcher: SdcProfileMatcher.matchSdcProfileUrl.bind(SdcProfileMatcher), // could be ':sdcKey/student/:studentId'(landing) or 'profile'(in-app routing)
          canActivate: [SdcActivityGuardForProfile], // will be redirectd to the new student ssv url
          component: SchoolSingleColumnLayoutComponent,
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
          },
        },
        {
          path: 'lists',
          component: LayoutSwitcherComponent,
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL, ENTITY_TYPE.SHELTER],
          },
          canActivateChild: [EntityTypeGuard],
          loadChildren: () =>
            import('../../../school/lists/school-lists-routing.module').then(m => m.SchoolListRoutingModule),
        },
        {
          path: 'doe-postsec-advising',
          component: LayoutSwitcherComponent,
          loadChildren: () =>
            import('../../../school/uft-doe-advising/uft-doe-advising.module').then(
              m => m.UftDoeAdvisingModule,
            ),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
          },
        },
        {
          path: 'uft-doe-advising',
          redirectTo: 'doe-postsec-advising',
        },
        {
          path: 'postsecondary',
          component: LayoutSwitcherComponent,
          canActivate: [
            FeatureToggleGuard,
          ],
          data: {
            toggles: ['TOGGLE_CONTENT_AREA_DASHBOARD'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.Postsecondary,
            pageTemplate: PageTemplate.Dashboard,
          },
          canActivateChild: [EntityTypeGuard],
          loadChildren: () =>
            import('../../../school/content-area/postsecondary/postsecondary-cad-routing.module').then(m => m.PostsecondaryCadRoutingModule),
        },
        {
          path: 'postsecondary-dashboard',
          component: LayoutSwitcherComponent,
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.Postsecondary,
            pageTemplate: PageTemplate.Dashboard,
          },
          loadChildren: () =>
            import('../../../school/content-area/postsecondary/postsecondary-dashboard.module').then(m => m.PostsecondaryDashboardModule),
        },
        {
          path: 'kims-tracker',
          component: LayoutSwitcherComponent,
          canActivate: [EntityTypeGuard, KimsTrackerAccessGuard],
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.PostsecondaryKims,
            pageTemplate: PageTemplate.Dashboard,
          },
          canActivateChild: [EntityTypeGuard],
          loadChildren: () =>
            import('../../../school/content-area/postsecondary/kims-tracker/kims-tracker.module').then(m => m.KimsTrackerModule),
        },
        {
          path: 'college-now',
          component: LayoutSwitcherComponent,
          canActivateChild: [EntityTypeGuard],
          loadChildren: () =>
            import('../../../school/content-area/college-now/college-now-cad-routing.module').then(m => m.CollegeNowCadRoutingModule),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.Postsecondary,
          },
        },
        {
          path: 'settings',
          component: SettingsLayoutComponent,
          resolve: {
            schoolResource: SchoolResourceResolver,
          },
          loadChildren: () =>
            import('../../../shared/components/settings/settings-routing.module').then(m => m.SettingsRoutingModule),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
          },
          canActivateChild: [EntityTypeGuard],
        },
        {
          path: 'user-management',
          component: LayoutSwitcherComponent,
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.UserManagement,
          },
          loadChildren: () =>
            import('../../../shared/components/settings/user-management-settings/user-management-single-entity-routing.module').then(m => m.UserManagementSingleEntityRoutingModule),
        },
        {
          path: 'user-permissions',
          component: LayoutSwitcherComponent,
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SHELTER],
            // The page is cluster-scoped: the single-entity URL shape carries no cluster id, and
            // the component's shelter branches would hand a null clusterId to the user modals.
            allowedEntityModes: [ENTITY_MODE.MULTI_ENTITY],
            featureSet: FeatureSet.UserManagement,
            pageTemplate: PageTemplate.List,
          },
          canActivateChild: [EntityTypeGuard],
          loadChildren: () =>
            import('../../../shared/components/settings/user-management-settings/user-management-multi-entity-routing.module').then(m => m.UserManagementMultiEntityRoutingModule),
        },
        {
          path: 'credits',
          component: LayoutSwitcherComponent,
          canActivate: [
            FeatureToggleGuard,
          ],
          data: {
            toggles: ['TOGGLE_CREDITS_DASHBOARD'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.Credits,
            pageTemplate: PageTemplate.Dashboard,
          },
          resolve: {
            schoolResource: SchoolResourceResolver,
          },
          loadChildren: () =>
            import('../../../school/content-area/credits/credits-dashboard.module').then(m => m.CreditsDashboardModule),
        },
        {
          path: 'credits-cad', // route for old credits CAD
          component: LayoutSwitcherComponent,
          canActivate: [
            FeatureToggleGuard,
          ],
          data: {
            toggles: ['TOGGLE_CREDIT_GAPS'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
          },
          resolve: {
            schoolResource: SchoolResourceResolver,
          },
          loadChildren: () =>
            import('../../../school/content-area/credits/credits-routing.module').then(m => m.CreditsRoutingModule),
        },
        // Redirect to new Credits dashboard URL
        {
          path: 'credits/setup',
          redirectTo: 'credits/settings',
          pathMatch: 'full',
        },
        {
          path: 'credits/monitor',
          redirectTo: 'credits',
          pathMatch: 'full',
        },
        {
          path: 'credits/respond',
          redirectTo: 'credits',
          pathMatch: 'full',
        },
        {
          path: 'graduation',
          component: LayoutSwitcherComponent,
          canActivate: [
            FeatureToggleGuard,
          ],
          data: {
            toggles: ['TOGGLE_GRADUATION'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.Graduation,
          },
          resolve: {
            schoolResource: SchoolResourceResolver,
          },
          loadChildren: () =>
            import('../../../school/content-area/graduation/graduation-routing.module').then(m => m.GraduationRoutingModule),
        },
        {
          path: 'graduation/projections',
          component: LayoutSwitcherComponent,
          canActivate: [
            FeatureToggleGuard,
          ],
          data: {
            toggles: ['TOGGLE_GRADUATION'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.GraduationProjections,
          },
          resolve: {
            schoolResource: SchoolResourceResolver,
          },
          loadChildren: () =>
            import('../../../school/content-area/graduation/graduation-projections/graduation-projections.module').then(m => m.GraduationProjectionsModule),
        },
        {
          path: 'regents',
          component: LayoutSwitcherComponent,
          canActivate: [
            FeatureToggleGuard,
          ],
          data: {
            toggles: ['TOGGLE_SDC_REGENTS'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.Regents,
          },
          resolve: {
            schoolResource: SchoolResourceResolver,
          },
          loadChildren: () =>
            import('../../../school/content-area/regents/regents-routing.module').then(m => m.RegentsRoutingModule),
        },
        {
          path: 'regents/regents-results',
          resolve: {
            schoolResource: SchoolResourceResolver,
          },
          loadChildren: () =>
            import('../../../school/content-area/regents/regents-results/regents-results.module').then(m => m.RegentsResultsModule),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.RegentsResults,
          },
        },
        {
          path: 'regents-results-grid',
          resolve: { gridState: GridStateResolver },
          loadChildren: () =>
            import('../../../school/content-area/regents/regents-results/regents-results-grid/regents-results-grid.module').then(m => m.RegentsResultsGridModule),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.RegentsResults,
          },
        },
        {
          path: 'mock-regents/overview',
          component: LayoutSwitcherComponent,
          resolve: {
            schoolResource: SchoolResourceResolver,
          },
          loadChildren: () =>
            import('../../../school/content-area/regents/mock-regents-splash/mock-regents-splash.module').then(m => m.MockRegentsSplashModule),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.MockAssessments,
            pageTemplate: PageTemplate.Overview,
          },
        },
        {
          path: 'mock-regents/results',
          component: LayoutSwitcherComponent,
          resolve: {
            schoolResource: SchoolResourceResolver,
          },
          loadChildren: () => import('../../../school/content-area/assessments/dashboard/assessments.module').then(m => m.AssessmentsModule), // temporary while we transition to new assessments dashboard
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.MockAssessments,
            pageTemplate: PageTemplate.Dashboard,
          },
        },
        {
          path: 'mock-regents/mock-regents-grid',
          resolve: {
            schoolResource: SchoolResourceResolver,
          },
          loadChildren: () => import('../../../school/content-area/mock-regents/mock-regents-grid.module').then(m => m.MockRegentsGridModule),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.MockAssessments,
          },
        },
        {
          path: 'mock-regents/verify',
          component: LayoutSwitcherComponent,
          resolve: {
            schoolResource: SchoolResourceResolver,
          },
          loadChildren: () => import('../../../school/content-area/regents/mock-regents-verify/mock-regents-verify.module').then(m => m.MockRegentsVerifyModule),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.MockAssessments,
          },
        },
        {
          path: 'academic',
          component: LayoutSwitcherComponent,
          canActivate: [
            FeatureToggleGuard,
          ],
          data: {
            toggles: ['TOGGLE_CONTENT_AREA_DASHBOARD'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.Academics,
          },
          loadChildren: () =>
            import('../../../school/content-area/academic/academic-cad-routing.module').then(m => m.AcademicCadRoutingModule),
        },
        {
          path: 'academic/pass-rates',
          resolve: {
            schoolResource: SchoolResourceResolver,
          },
          loadChildren: () =>
            import('../../../school/content-area/academic/pass-rates/pass-rates.module').then(m => m.PassRatesModule),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.Academics,
          },
        },
        {
          path: 'pass-rates-grid',
          resolve: {
            schoolResource: SchoolResourceResolver,
          },
          loadChildren: () =>
            import('../../../school/content-area/academic/pass-rates/course-marks-grid/course-marks-grid.module').then(m => m.CourseMarksGridModule),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.PassRates,
          },
        },
        {
          path: 'attendance/dashboard',
          component: LayoutSwitcherComponent,
          canActivate: [FeatureToggleGuard],
          data: {
            toggles: ['TOGGLE_ONE_PORTAL'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL, ENTITY_TYPE.SHELTER],
            contentArea: 'attendance-dashboard',
            featureSet: FeatureSet.Attendance,
            pageTemplate: PageTemplate.Dashboard,
          },
          loadChildren: () =>
            import('../op-dashboard/op-dashboard-routing.module').then(m => m.OpDashboardRoutingModule),
        },
        // One route: HomeSwitcherComponent picks the surface at render time from the evaluation
        // EntityResourceGuard cached, replacing two siblings whose canMatch recomputed it during
        // route recognition, before any guard had run.
        {
          path: 'home',
          component: LayoutSwitcherComponent,
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL, ENTITY_TYPE.SHELTER],
            featureSet: FeatureSet.Home,
            pageTemplate: PageTemplate.Home,
            // The op-dashboard presentation inputs live on HomeSwitcher's template, not here:
            // op-dashboard reads `data.applyGlobalFilter ?? input`, so route data outranks the
            // input and would reach the tiles variant's embedded rows too.
            //
            // Three surfaces read the school under this route, not one: TilesComponent, and the
            // legacy chrome LayoutSwitcher renders around it for a One Portal-off district —
            // SchoolStandardLayoutComponent and the side nav inside it. All three await the load
            // rather than reading the slice. The OP dashboard does not read it at all.
            //
            // The layout is the part that is easy to miss, and did get missed: a snapshot there
            // lands on the empty slice a district switch leaves behind, and never sees the
            // document arrive. Count the chrome, not just the routed component, before adding a key.
            defer: [DeferrableLoad.School],
          },
          canActivateChild: [EntityTypeGuard],
          loadChildren: () =>
            import('../../../school/home-switcher/home-switcher-routing.module').then(m => m.HomeSwitcherRoutingModule),
        },
        {
          path: 'demographics/dashboard',
          component: LayoutSwitcherComponent,
          canActivate: [FeatureToggleGuard],
          data: {
            toggles: ['TOGGLE_ONE_PORTAL'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL, ENTITY_TYPE.SHELTER],
            contentArea: 'demographics-dashboard',
            featureSet: FeatureSet.Demographics,
            pageTemplate: PageTemplate.Dashboard,
          },
          loadChildren: () =>
            import('../op-dashboard/op-dashboard-routing.module').then(m => m.OpDashboardRoutingModule),
        },
        {
          path: 'ecfik/dashboard',
          component: LayoutSwitcherComponent,
          canActivate: [FeatureToggleGuard],
          data: {
            toggles: ['TOGGLE_ONE_PORTAL'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            contentArea: 'ecfik-dashboard',
            featureSet: FeatureSet.ECFIK,
            pageTemplate: PageTemplate.Dashboard,
          },
          loadChildren: () =>
            import('../op-dashboard/op-dashboard-routing.module').then(m => m.OpDashboardRoutingModule),
        },
        {
          path: 'ecfik/breakdown',
          component: LayoutSwitcherComponent,
          canActivate: [FeatureToggleGuard],
          data: {
            toggles: ['TOGGLE_ONE_PORTAL'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.ECFIK,
            pageTemplate: PageTemplate.Breakdown,
          },
          loadChildren: () =>
            import('../../../school/content-area/tertiary/tertiary-routing.module').then(m => m.TertiaryRoutingModule),
        },
        {
          path: 'families/dashboard',
          component: LayoutSwitcherComponent,
          canActivate: [FeatureToggleGuard],
          data: {
            toggles: ['TOGGLE_ONE_PORTAL', 'TOGGLE_FAMICOM'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            contentArea: 'families-dashboard',
            featureSet: FeatureSet.FamilyCommunication,
            pageTemplate: PageTemplate.Dashboard,
          },
          loadChildren: () =>
            import('../../../school/families-dashboard/families-dashboard-routing.module').then(m => m.FamiliesDashboardRoutingModule),
        },
        {
          path: 'demographics/breakdown',
          component: LayoutSwitcherComponent,
          canActivate: [FeatureToggleGuard],
          data: {
            toggles: ['TOGGLE_ONE_PORTAL'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL, ENTITY_TYPE.SHELTER],
            featureSet: FeatureSet.Demographics,
            pageTemplate: PageTemplate.Breakdown,
          },
          loadChildren: () =>
            import('../../../school/content-area/tertiary/tertiary-routing.module').then(m => m.TertiaryRoutingModule),
        },
        {
          path: 'attendance',
          component: LayoutSwitcherComponent,
          canActivate: [
            FeatureToggleGuard,
          ],
          data: {
            toggles: ['TOGGLE_ATTENDANCE_CONTENT_AREA_DASHBOARD'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
          },
          loadChildren: () =>
            import('../../../school/content-area/attendance/attendance-cad-routing.module').then(m => m.AttendanceCadRoutingModule),
        },
        {
          path: 'attendance/breakdown',
          component: LayoutSwitcherComponent,
          canActivate: [FeatureToggleGuard],
          data: {
            toggles: ['TOGGLE_ONE_PORTAL'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL, ENTITY_TYPE.SHELTER],
            featureSet: FeatureSet.Attendance,
            pageTemplate: PageTemplate.Breakdown,
          },
          loadChildren: () =>
            import('../../../school/content-area/tertiary/tertiary-routing.module').then(m => m.TertiaryRoutingModule),
        },
        {
          path: 'my-classes',
          component: LayoutSwitcherComponent,
          canActivate: [FeatureToggleGuard],
          data: {
            toggles: ['TOGGLE_ONE_PORTAL'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
          },
          canActivateChild: [EntityTypeGuard],
          loadChildren: () =>
            import('../../../school/my-classes/my-classes-routing.module').then(m => m.MyClassesRoutingModule),
        },
        {
          path: 'screeners',
          component: LayoutSwitcherComponent,
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.Screeners,
          },
          loadChildren: () =>
            import('../../../school/content-area/screeners/screeners-routing.module').then(m => m.ScreenersRoutingModule),
        },
        {
          path: 'screeners-grid-results',
          resolve: { gridState: GridStateResolver },
          loadComponent: () =>
            import('../../../school/content-area/screeners/grid-results/screeners-grid-results.component').then(m => m.ScreenersGridResultsComponent),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.Screeners,
          },
        },
        {
          path: 'students',
          component: LayoutSwitcherComponent,
          canActivate: [
            FeatureToggleGuard,
          ],
          data: {
            toggles: ['TOGGLE_CONTENT_AREA_DASHBOARD'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
          },
          loadChildren: () =>
            import('../../../school/content-area/students/students-cad-routing.module').then(m => m.StudentsCadRoutingModule),
        },
        {
          path: 'core-4',
          redirectTo: 'alerts',
        },
        {
          path: 'alerts',
          component: LayoutSwitcherComponent,
          canActivate: [
            FeatureToggleGuard,
          ],
          data: {
            toggles: ['TOGGLE_CONTENT_AREA_DASHBOARD'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.Core4,
          },
          loadChildren: () =>
            import('../../../school/content-area/core-4/core-4-cad-routing.module').then(m => m.Core4CadRoutingModule),
        },
        {
          matcher: ssvMatcher, // could be '' or :studentId based on existing logic
          component: LayoutSwitcherComponent,
          resolve: {
            filter: SchoolSsvFilterResolver,
            sort: SchoolSsvSortResolver,
          },
          // A global-search pick from an open profile can change only `?filter=` (the position
          // segment is absent on picks), and the default re-runs resolvers on path changes alone —
          // leaving `data.filter` pointing at the previous student list. Both resolvers are pure
          // ObjectCache parses, so re-running them on a queryParams change is a cache hit.
          runGuardsAndResolvers: 'paramsOrQueryParamsChange',
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL, ENTITY_TYPE.SHELTER],
            legacyLayoutVariant: 'singleColumn',
            featureSet: FeatureSet.Profile,
          },
          loadChildren: () =>
            import('../../../student/student.module').then(m => m.StudentModule),
        },
        {
          // Shared school/shelter entity profile, e.g. `.../multi/:networkId/school/:entityId`.
          // Rendered by StudentComponent -> op-profile-container, which branches on
          // entityMode (see StudentComponent.initMultiEntitySchoolProfile).
          matcher: entityProfileMatcher,
          component: LayoutSwitcherComponent,
          resolve: {
            filter: SchoolSsvFilterResolver,
            sort: SchoolSsvSortResolver,
          },
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL, ENTITY_TYPE.SHELTER],
            legacyLayoutVariant: 'singleColumn',
            featureSet: FeatureSet.Profile,
            refreshComponent: (future, curr) =>
              UrlNavigationService.getEntityId(future) !== UrlNavigationService.getEntityId(curr),
          },
          loadChildren: () =>
            import('../../../student/student.module').then(m => m.StudentModule),
        },
        {
          // Family profile, e.g. `.../school/single/:schoolId/family`; familyId travels
          // as a query param, not a path segment. Same matcher shape as ssvMatcher, so it
          // also resolves under `multi/:networkId` once FamilyProfileComponent branches on
          // entityMode — not yet built.
          matcher: familyMatcher,
          component: LayoutSwitcherComponent,
          canActivate: [
            FeatureToggleGuard,
          ],
          resolve: {
            schoolResource: SchoolResourceResolver,
          },
          data: {
            toggles: ['TOGGLE_ONE_PORTAL', 'TOGGLE_FAMICOM'],
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.Family,
            pageTemplate: PageTemplate.Profile,
          },
          loadChildren: () =>
            import('../../../school/family-profile/family-profile-routing.module').then(m => m.FamilyProfileRoutingModule),
        },
        {
          path: 'data-entry',
          loadChildren: () => import('../../../school/data-entry/data-entry.module').then(m => m.DataEntryModule),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
          },
        },
        {
          path: 'grad-planning',
          loadChildren: () =>
            import('../../../school/sdc/grad-planning/grad-planning-container.module').then(
              m => m.GradPlanningContainerModule,
            ),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
          },
        },
        {
          path: 'regents-planning',
          loadChildren: () =>
            import('../../../school/sdc/regents-planning/regents-planning-container.module').then(
              m => m.RegentsPlanningContainerModule,
            ),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
          },
        },
        {
          path: 'regents-prep',
          loadChildren: () =>
            import('../../../school/sdc/regents-prep/regents-prep-container.module').then(
              m => m.RegentsPrepContainerModule,
            ),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
          },
        },
        {
          path: 'data-grid',
          component: LayoutSwitcherComponent,
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL, ENTITY_TYPE.SHELTER],
            featureSet: FeatureSet.Grid,
            // When One Portal is off, render the grid with no layout wrapper so it
            // stays full-screen exactly as it did before One Portal.
            legacyLayoutVariant: 'fullScreen',
          },
          loadChildren: () =>
            import('../../../school/server-side-grid/server-side-grid.module').then(m => m.ServerSideGridModule),
        },
        {
          path: 'default-sdc',
          loadChildren: () =>
            import('../../../school/sdc/default-sdc/default-sdc-container.module').then(
              m => m.DefaultSdcContainerModule,
            ),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
          },
        },
        {
          path: 'data-uploads',
          loadChildren: () => import('../../../school/data-uploads/data-uploads.module').then(m => m.DataUploadsModule),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
          },
        },
        {
          path: 'other-tools',
          component: OtherToolsLayoutComponent,
          resolve: {
            schoolResource: SchoolResourceResolver,
          },
          loadChildren: () => import('../../../school/school-tools/other-tools/other-tools-routing.module').then(m => m.OtherToolsRoutingModule),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
          },
        },
        {
          path: 'manage-families',
          component: LayoutSwitcherComponent, // pass through
          resolve: {
            schoolResource: SchoolResourceResolver,
          },
          loadChildren: () =>
            import('../../../school/ecfik-management/ecfik-management.module').then(m => m.ECFIKManagementModule),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.ECFIK,
          },
        },
        {
          path: 'manage-program',
          component: LayoutSwitcherComponent, // pass through
          resolve: {
            schoolResource: SchoolResourceResolver,
          },
          loadChildren: () =>
            import('../../../school/ecfik-management/ecfik-manage-program/ecfik-manage-program-routing.module').then(m => m.ECFIKManageProgramRoutingModule),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.ECFIK,
          },
        },
        {
          path: 'ecfik-needs',
          component: LayoutSwitcherComponent,
          resolve: {
            schoolResource: SchoolResourceResolver,
          },
          loadChildren: () =>
            import('../../../school/ecfik-management/family-checkins-needs/family-checkins-needs.module').then(m => m.FamilyCheckinsNeedsModule),
          data: {
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.ECFIK,
          },
        },
        {
          path: 'notes',
          component: LayoutSwitcherComponent,
          data: {
            onFailureRedirect: 'home',
            origin: 'Notes Inbox',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL, ENTITY_TYPE.SHELTER],
            featureSet: FeatureSet.Notes,
          },
          loadChildren: () =>
            import('../../../school/notes/notes-page/notes-page.module').then(m => m.NotesPageModule),
        },
        {
          path: 'my-caseload',
          data: {
            onFailureRedirect: 'home',
            allowedEntityTypes: [ENTITY_TYPE.SCHOOL],
            featureSet: FeatureSet.UserManagement,
          },
          loadChildren: () =>
            import('../../../school/caseload/my-caseload/my-caseload.module').then(m => m.MyCaseloadModule),
        },
        // Redirect to new Mock Regents url
        {
          path: 'lists/mock-regents',
          redirectTo: 'mock-regents/results',
          pathMatch: 'full',
        },
        {
          path: 'regents/mock-regents',
          redirectTo: 'mock-regents/results',
          pathMatch: 'full',
        },
        {
          path: 'regents/mock-regents-grid',
          redirectTo: 'mock-regents/mock-regents-grid',
          pathMatch: 'full',
        },
        // Redirect to new Screeners url
        {
          path: 'lists/screeners-list',
          redirectTo: 'screeners',
          pathMatch: 'full',
        },
        // Redirect to new Notes url
        {
          path: 'my-tasks/created-by-me',
          redirectTo: 'notes',
          pathMatch: 'full',
        },
        {
          path: 'my-tasks/assigned-to-me',
          redirectTo: 'notes',
          pathMatch: 'full',
        },
        {
          path: 'my-tasks',
          redirectTo: 'notes',
          pathMatch: 'full',
        },
      ],
    },
  ],
};
